import React, { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Headline } from '@xingternal/typography';

import { useTrackPage } from '@/hooks/useTrackPage';

import { FollowButton } from '@/components/FollowButton';
import { CompanyCard } from '@/components/CompanyCard';
import { CompanyCardContainer } from '@/components/CompanyCardContainer';
import styles from './FollowedCompanies.css';
import { ShowMoreButton } from '@/components/ShowMoreButton';
import { CompanyCardsSkeletonLoggedin } from '@/components/Skeleton';
import { MetaData } from '@/components/MetaData';
import { PageError } from '@/components/PageError';
import { useFollowedCompanies } from '@/hooks/useFollowedCompanies';
import { useCompanyData } from '@/contexts/CompaniesDataContext';
import { pagesTrackingName } from '@/utils/tracking';
import { FollowedEmptyState } from './FollowedEmptyState';
import { Company } from '@/types/graphql-types';

const FollowedCompanies: React.FC = () => {
  const { data, loading, error, networkStatus, pagination } =
    useFollowedCompanies();
  const { addCompanies, globalCompaniesState } = useCompanyData();

  const currentCompanies = data?.viewer?.followedCompanies?.edges;
  useEffect(() => {
    if (data?.viewer?.followedCompanies?.edges) {
      addCompanies({
        companies: data.viewer.followedCompanies.edges.map((item) => {
          return item?.node?.company;
        }) as Company[],
      });
    }
  }, [currentCompanies]);
  const intl = useIntl();

  useTrackPage({ pageName: pagesTrackingName.followed });

  const loadingFirstRender = loading && networkStatus !== 3;
  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;
  if (currentCompanies?.length === 0) return <FollowedEmptyState />;

  const { hasNextPage, endCursor } =
    data?.viewer?.followedCompanies?.pageInfo ?? {};

  const isPaginationDisabled = () => {
    if (loadingFirstRender) return true;

    return false;
  };

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({
          id: 'CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW',
        })}
        description={intl.formatMessage({
          id: 'CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW',
        })}
      />
      <Headline size="medium" className={styles.headline}>
        <FormattedMessage id="CP_100_MYCP_FOLLOWED_COMPANIES_TITLE_NEW" />
      </Headline>
      <CompanyCardContainer>
        {currentCompanies?.map((item) => {
          const company = item?.node?.company as Company;
          const isFollowing =
            company?.userContext?.followState?.isFollowing || false;
          return (
            <CompanyCard
              key={company?.id}
              company={company}
              buttons={
                globalCompaniesState.length > 0
                  ? [
                      <FollowButton
                        key={company?.id}
                        className={styles.followButton}
                        disabled={company?.entityPageId ? false : true}
                        entityPageId={company?.entityPageId as string}
                        isFollowing={isFollowing}
                        companyId={company?.id}
                        followText="CP_200_FOLLOW_LINK_BACK"
                      />,
                    ]
                  : []
              }
            />
          );
        })}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => pagination(endCursor as string)}
          intlKey="CP_OTHER_COMPANIES_FOLLOW"
        />
      )}
    </>
  );
};

export { FollowedCompanies };
